import { Badge, Col, Container, Row } from "reactstrap";
import MainLayout from "../../layouts/MainLayout";
import { API_URL, STORAGE_URL } from "../../utils/constants";
import { useEffect, useState } from "react";
import { ReactComponent as EmptyIcon } from '../../assets/images/icons/empty.svg';
import PropertyCard from "../../components/Cards/PropertyCard";
import { CiFilter } from "react-icons/ci";
import DefaultButton from "../../components/Buttons/Default";
import SearchFiltersModal from "../../components/Modals/SearchFilters";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import './styles.css';

function SearchPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const searchParams = new URLSearchParams(window.location.search);
  const propertyFor = searchParams.get('property_for');
  const categoryId = searchParams.get('category_id');
  const minPrice = searchParams.get('min_price');
  const maxPrice = searchParams.get('max_price');
  const cityIds = searchParams.getAll('city_ids[]');
  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [properties, setProperties] = useState([]);
  const [totalProperties, setTotalProperties] = useState(0);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [categories, setCategories] = useState([]);
  const [states, setStates] = useState([]);
  const [contentAds, setContentAds] = useState(null);

  const getProperties = (page = 1, append = false) => {
    const baseCacheKey = `search_data_${location.search}`;
    const cachedData = sessionStorage.getItem(baseCacheKey);

    // Check if we have cached data and we're loading the first page
    if (!append && page === 1 && cachedData) {
      const parsed = JSON.parse(cachedData);
      setProperties(parsed.properties);
      setTotalProperties(parsed.totalProperties);
      setCategories(parsed.categories);
      setStates(parsed.states);
      setContentAds(parsed.contentAds);
      setHasMorePages(parsed.hasMorePages);
      setCurrentPage(parsed.currentPage);
      setPropertiesLoading(false);
      return;
    }

    if (append) {
      setLoadingMore(true);
    } else {
      setPropertiesLoading(true);
    }

    let url = `${API_URL}/search`;
    const params = [];
    
    if (propertyFor) {
      params.push(`property_for=${propertyFor}`);
    }
    if (categoryId) {
      params.push(`category_id=${categoryId}`);
    }
    if (cityIds.length > 0) {
      params.push(`city_ids=${cityIds}`);
    }
    if (minPrice) {
      params.push(`min_price=${minPrice}`);
    }
    if (maxPrice) {
      params.push(`max_price=${maxPrice}`);
    }
    
    params.push(`page=${page}`);
    
    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {      
      if (data.success) {
        let updatedProperties;
        
        if (append) {
          updatedProperties = [...properties, ...data.properties.data];
          setProperties(updatedProperties);
        } else {
          updatedProperties = data.properties.data;
          setProperties(updatedProperties);
        }

        setTotalProperties(data?.properties?.total || 0);
        setCategories(data.categories);
        setStates(data.states);
        setContentAds(data.content_ads || null);
        
        // Check if there are more pages
        const morePages = data.properties.current_page < data.properties.last_page;
        setHasMorePages(morePages);
        setCurrentPage(data.properties.current_page);

        // Always update cache with all loaded properties
        const cacheData = {
          properties: updatedProperties,
          totalProperties: data?.properties?.total || 0,
          categories: data.categories,
          states: data.states,
          contentAds: data.content_ads || null,
          hasMorePages: morePages,
          currentPage: data.properties.current_page
        };
        sessionStorage.setItem(baseCacheKey, JSON.stringify(cacheData));
      }
      setPropertiesLoading(false);
      setLoadingMore(false);
    })
    .catch(error => {
      console.error('Error fetching properties:', error);
      setPropertiesLoading(false);
      setLoadingMore(false);
    });
  }

  const loadMore = () => {
    if (!loadingMore && hasMorePages) {
      getProperties(currentPage + 1, true);
    }
  }

  // Infinite scroll handler
  useEffect(() => {
    const handleScroll = () => {
      if (loadingMore || !hasMorePages) return;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight - 200) {
        loadMore();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadingMore, hasMorePages, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
    getProperties(1, false);
  }, [location.search]);  

  const filterCount = (categoryId ? 1 : 0) + cityIds.length + (minPrice ? 1 : 0) + (maxPrice ? 1 : 0);

  return(
    <MainLayout>
      <section className="mb-4">
        <div className="search-sticky-header">
          <Container>
            <Row className="align-items-center justify-content-center">
              <Col lg="6" xs="7">
                <h4 className="fw-normal mb-0">
                  {t('search.title')}{' '}
                  <small className="text-muted">( {totalProperties} )</small>
                </h4>
              </Col>
              <Col lg="6" xs="5" className="text-end">
                <DefaultButton
                  size="md"
                  color={filterCount > 0 ? "primary" : "light"}
                  className={filterCount > 0 ? "" : "border border-dark"}
                  onClick={() => setFiltersModalOpen(true)}
                >
                  <CiFilter size={20} /> {t('search.filter_button')}
                  {filterCount > 0 && (
                    <span className="badge bg-white text-primary rounded-pill ms-2">{filterCount}</span>
                  )}
                </DefaultButton>
              </Col>
            </Row>
          </Container>
        </div>

        <Container className="pt-3">
          {propertiesLoading ?
            <div className="loader-container">
              <div className="loader"></div>
            </div>
          :
            <>
              <Row className="g-3">
                {contentAds && (
                  <Col xl="3" lg="4" md="4" sm="6" xs="12">
                    <div className="property-card position-relative h-100" style={{background:'linear-gradient(135deg,#fafffe,#f0f9f4)'}}>
                      <Link to={contentAds.url} className="stretched-link" target="_blank"></Link>
                      <div className="property-card__image-wrap">
                        <img src={STORAGE_URL + contentAds.image_url} alt="" className="property-card__image" />
                        <span className="property-card__badge property-card__badge--paused" style={{background:'rgba(0,0,0,0.45)'}}>ADS</span>
                      </div>
                      <div className="property-card__body">
                        <h5 className="property-card__title">{contentAds.title}</h5>
                      </div>
                    </div>
                  </Col>
                )}

                {properties?.length > 0 ?
                  properties.map((data, key) => (
                    <Col xl="3" lg="4" md="4" sm="6" xs="12" key={key}>
                      <PropertyCard property={data} />
                    </Col>
                  ))
                :
                  <Col xs="12" className="text-center py-5">
                    <EmptyIcon />
                    <h6 className="mt-4 fw-normal text-muted">لا توجد عقارات</h6>
                  </Col>
                }
              </Row>

              {loadingMore && (
                <Row className="mb-5">
                  <Col xs="12" className="text-center">
                    <div className="loader mx-auto"></div>
                  </Col>
                </Row>
              )}
            </>
          }
        </Container>
      </section>

      <SearchFiltersModal
        isOpen={filtersModalOpen}
        toggle={() => setFiltersModalOpen(!filtersModalOpen)}
        categories={categories}
        states={states}
      />
    </MainLayout>
  );
}

export default SearchPage;